import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { patientsMedicationsApi } from "apis";
import { EDefaultValues } from "constants/defaultValues";
import React from "react";

import { useCelleraTheme } from "../../../../theme/useCelleraTheme";
import IntakeHeader from "../IntakeHeader";

interface MedicationsProps {
  patientId: string;
}

const Medications: React.FC<MedicationsProps> = ({ patientId }) => {
  const { colors } = useCelleraTheme();
  const { data: medications, isFetching } = useQuery({
    queryKey: ["medications", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return patientsMedicationsApi.getMedicationStatementsByPatientId({
        patientId,
      });
    },
    enabled: !!patientId,
  });

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="flex justify-center items-center">
          <CircularProgress size={36} color="primary" />
        </div>
      );
    }

    if (!medications || medications.length === 0) {
      return (
        <div className="flex justify-center items-center">
          <Typography variant="body1">No medications found</Typography>
        </div>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {medications.map((medication, index) => (
          <Box
            key={medication.id}
            sx={{
              backgroundColor: colors.background.paper,
              border: `1px solid ${colors.border.light}`,
              borderRadius: "8px",
              p: 2,
            }}
          >
            {/* Medication Title with Number */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
              }}
            >
              #{index + 1} <span className="capitalize">{medication.name}</span>
            </Typography>

            {/* Medication Details */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" sx={{ color: colors.text.primary }}>
                Dose: {medication.dose || EDefaultValues.EMPTY_STRING}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary }}>
                Frequency: {medication.frequency || EDefaultValues.EMPTY_STRING}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary }}>
                Purpose: {medication.purpose || EDefaultValues.EMPTY_STRING}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <IntakeHeader title="Current Medications & Supplements" />
      {renderContent()}
    </>
  );
};

export default Medications;
